import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Coffee, Pause, Play, Plus, Repeat, SkipForward, Square } from 'lucide-react';

import { cn } from '@/lib/cn';
import { motionSafe, springDefault, springSheet } from '@/lib/motion';
import { TaskSwitcher } from './TaskSwitcher';
import {
  formatCountdown,
  requestNotificationPermission,
  useBoundaryAlert,
  useCountdown,
  useFocus,
  useFocusControls,
  useFocusSessionSync,
} from './useFocus';
import { formatClock, useElapsed } from './useTimer';

/**
 * The persistent focus bar.
 *
 * Never blocks input, never opens a dialog on you, never steals focus. A
 * Pomodoro tool that interrupts with a modal is one you turn off in week two.
 */
export function FocusBar() {
  const focus = useFocus();
  const controls = useFocusControls();
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const state = focus.data;
  useBoundaryAlert(state);
  useNotificationPrompt(Boolean(state && state.mode !== 'idle'));

  const remaining = useCountdown(state?.session_ends_at ?? null);
  const liveTaskSeconds = useElapsed(state?.task_started_at);
  useFocusSessionSync(state?.session_ends_at ?? null, remaining, () => {
    void focus.refetch();
  });

  // `S` switches task from anywhere. The escape hatch has to be faster than
  // the impulse that triggers it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 's' || event.metaKey || event.ctrlKey || event.altKey) return;
      const el = event.target as HTMLElement | null;
      if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable) return;
      if (!state || (state.mode === 'idle' && !state.task_started_at)) return;
      event.preventDefault();
      setSwitcherOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  // Show whenever a Pomodoro session or a client/task timer is open. TimeFlip
  // and Mac can leave mode idle while a client entry is still running.
  const timerOpen =
    Boolean(state?.task_started_at) || (state?.task_elapsed_seconds ?? 0) > 0;
  if (!state || (state.mode === 'idle' && !timerOpen)) return null;

  const onBreak = state.mode === 'on_break';
  const paused = state.mode === 'paused';
  const trackingOnly = state.mode === 'idle' && timerOpen;
  const title =
    state.active_task_title ??
    state.resume_task_title ??
    state.active_calendar_event_title ??
    (state.active_client_name
      ? state.active_project_name
        ? `${state.active_client_name} · ${state.active_project_name}`
        : state.active_client_name
      : null);
  const hasPomodoroSession = Boolean(state.session_ends_at && state.pomodoro_enabled);
  const taskSeconds = Math.max(liveTaskSeconds, state.task_elapsed_seconds);

  const total =
    hasPomodoroSession && state.session_kind === 'work'
      ? state.settings.work_minutes * 60
      : hasPomodoroSession && state.session_kind === 'long_break'
        ? state.settings.long_break_minutes * 60
        : hasPomodoroSession
          ? state.settings.short_break_minutes * 60
          : 0;
  const progress = total > 0 ? 1 - remaining / total : 0;

  return (
    <>
      <motion.div
        initial={{ y: 72, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 72, opacity: 0 }}
        transition={motionSafe(springSheet)}
        className={cn(
          'material fixed inset-x-0 z-chrome border-t',
          // Sit above the mobile tab bar (0 on desktop).
          'bottom-[var(--tab-bar-height,0px)]',
          'pb-[var(--focus-bar-pad,max(0.5rem,env(safe-area-inset-bottom,0px)))]',
        )}
      >
        {/* Progress is a scaleX transform on its own layer, driven from the
            server's deadline. Animating width would reflow every frame. */}
        <div className="h-0.5 w-full bg-surface-3">
          {hasPomodoroSession && (
            <motion.div
              className={cn('h-full origin-left', onBreak ? 'bg-focus-break' : 'bg-accent')}
              style={{ scaleX: progress }}
              transition={motionSafe(springDefault)}
            />
          )}
        </div>

        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-2">
          {/* Working time wears the client's colour, the way rows in the
              backlog do, so the bar says whose hour this is without a label.
              Breaks and pauses keep the system colours: they are not billable
              to anyone. */}
          <span
            aria-hidden
            className={cn(
              'size-dot shrink-0 rounded-full',
              onBreak ? 'bg-focus-break' : paused ? 'bg-text-tertiary' : 'animate-pulse bg-accent',
            )}
            style={
              !onBreak && !paused && state.effective_client_color
                ? { backgroundColor: state.effective_client_color }
                : undefined
            }
          />

          <div className="min-w-0 flex-1">
            <p className="truncate text-small font-medium">
              {onBreak ? (
                <span className="text-focus-break">
                  <Coffee size={12} strokeWidth={1.5} className="mr-1 inline" aria-hidden />
                  Break
                </span>
              ) : paused ? (
                'Paused'
              ) : trackingOnly ? (
                title ?? 'Tracking'
              ) : (
                title
              )}
            </p>
            {/* The task stays named through the whole break. Losing the thread
                during a break is the entire failure mode. */}
            {(onBreak || paused) && title && (
              <p className="truncate text-label text-text-tertiary">{title}</p>
            )}
          </div>

          <span
            className={cn(
              'tabular shrink-0 text-body font-medium',
              onBreak && 'text-focus-break',
            )}
          >
            {hasPomodoroSession
              ? formatCountdown(remaining)
              : formatClock(taskSeconds)}
          </span>

          <div className="flex shrink-0 items-center gap-1">
            {onBreak && (
              <BarButton
                onClick={() => controls.skipBreak.mutate()}
                label="Skip break"
                icon={<SkipForward size={15} strokeWidth={1.5} aria-hidden />}
              />
            )}

            {paused && state.resume_task_id && (
              <button
                type="button"
                onClick={() => controls.resume.mutate()}
                className={cn(
                  'pressable inline-flex min-h-touch items-center gap-2 rounded px-3',
                  'bg-accent text-small font-medium text-text-on-accent hover:bg-accent-hover',
                )}
              >
                <Play size={14} strokeWidth={1.5} aria-hidden />
                <span className="max-w-32 truncate">Resume</span>
              </button>
            )}

            {state.mode === 'working' && (
              <>
                {/* Desktop: full control strip. Mobile: keep pause + overflow. */}
                <span className="hidden items-center gap-1 sm:contents">
                  <BarButton
                    onClick={() => controls.extend.mutate({ minutes: 5 })}
                    label="Add 5 minutes"
                    icon={<Plus size={15} strokeWidth={1.5} aria-hidden />}
                  />
                  <BarButton
                    onClick={() => setSwitcherOpen(true)}
                    label="Switch task (S)"
                    icon={<Repeat size={15} strokeWidth={1.5} aria-hidden />}
                  />
                </span>
                <span className="sm:hidden">
                  <BarButton
                    onClick={() => setSwitcherOpen(true)}
                    label="Switch task"
                    icon={<Repeat size={15} strokeWidth={1.5} aria-hidden />}
                  />
                </span>
                <BarButton
                  onClick={() => controls.pause.mutate()}
                  label="Pause"
                  icon={<Pause size={15} strokeWidth={1.5} aria-hidden />}
                />
              </>
            )}

            <BarButton
              onClick={() => controls.stop.mutate()}
              label={trackingOnly ? 'Stop tracking' : 'Stop focus'}
              icon={<Square size={13} strokeWidth={0} fill="currentColor" aria-hidden />}
            />
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {switcherOpen && (
          <TaskSwitcher
            onClose={() => setSwitcherOpen(false)}
            onPick={(taskId) => {
              controls.switchTask.mutate({ task_id: taskId });
              setSwitcherOpen(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function BarButton({
  onClick,
  label,
  icon,
}: {
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'pressable flex size-action items-center justify-center rounded-full',
        'text-text-secondary hover:bg-surface-3 hover:text-text',
      )}
    >
      {icon}
    </button>
  );
}

/** Ask for notification permission on the first real focus start, not on load. */
export function useNotificationPrompt(active: boolean) {
  useEffect(() => {
    if (active) requestNotificationPermission();
  }, [active]);
}
